// eslint-disable-next-line no-unused-vars
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';

import { NWTabsNavigation, NWNotification } from 'Components/shared';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';
import AccountProfile from 'Containers/Account/AccountProfile';
import AccountPersonalData from 'Containers/Account/AccountPersonalData';
import AccountSettings from 'Containers/Account/AccountSettings';
import AccountInfo from 'Containers/Account/AccountInfo';
import AccountCompanyData from 'Containers/Account/AccountCompanyData';
import useUser from 'Hooks/useUser';

const StyledAccount = styled.div``;

const AccountComponent = () => {
  const [errors, setErrors] = useState([]);
  const theme = useTheme();
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { data: { isImpersonated } = {} } = useUser();
  const accountFeatures = can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const canAccessCompanyData = !isImpersonated && accountFeatures;
  const notFound = <Navigate to="/not-found" replace />;
  const links = useMemo(
    () => [
      {
        to: 'profile',
        label: t('profile.menu.profile')
      },
      {
        to: 'settings',
        label: t('profile.menu.settings')
      },
      {
        to: 'personal-data',
        label: t('profile.menu.personalData')
      },
      ...(canAccessCompanyData
        ? [{
          to: 'company-data',
          label: t('offers.companyDataTab')
        }]
        : []),
      ...(accountFeatures
        ? [
          {
            to: 'info',
            label: t('profile.menu.info')
          }
        ]
        : []),
    ],
    [accountFeatures, canAccessCompanyData, t]
  );

  return (
    <StyledAccount theme={ theme }>
      <NWNotification delay={ 10 } errors={ errors } />
      <div className="pageTitle">
        <h3>{ t('profile.menu.account') }</h3>
      </div>
      <NWTabsNavigation links={ links } />
      <Routes>
        <Route index element={ <Navigate to="profile" replace /> } />
        <Route path="profile" element={ <AccountProfile setErrors={ setErrors } /> } />
        <Route path="settings" element={ <AccountSettings setErrors={ setErrors } /> } />
        <Route path="personal-data" element={ <AccountPersonalData setErrors={ setErrors } /> } />
        <Route
          path="company-data"
          element={ canAccessCompanyData
            ? <AccountCompanyData setErrors={ setErrors } />
            : notFound }
        />
        <Route path="info" element={ accountFeatures ? <AccountInfo /> : notFound } />
        <Route path="*" element={ <Navigate to="/not-found" replace /> } />
      </Routes>
    </StyledAccount>
  );
};

AccountComponent.displayName = 'AccountComponent';

export default AccountComponent;
